import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  changeClient360ContactStatus,
  classifyClient,
  createClient360Contact,
  updateClient360Contact,
  createOutlet,
  fetchClient360,
  fetchClient360Contacts,
  fetchClient360Overview,
  fetchClientList,
  fetchClientTab,
  fetchLegalEntityDetail,
  fetchStructure,
  fetchOutlets,
  updateOutlet,
  fetchNpAreas,
  fetchNpSettlements,
  fetchNpStreets,
  requestClient360ContactReview,
  type UpdateOutletPayload,
  type ClassifyClientPayload,
  type Client360ContactCreatePayload,
  type Client360ContactUpdatePayload,
  type ClientListQuery,
  type ClientTabKey,
  type ContactStatus,
  type CreateOutletPayload,
} from "@/api/clients";

const ONE_MINUTE = 60 * 1000;

/** A1 — holding-first Контрагенти list. */
export function useClientList(query: ClientListQuery) {
  return useQuery({
    queryKey: [
      "clientList",
      query.cursor ?? "",
      query.q ?? "",
      query.scope ?? "my",
      query.manager ?? "",
      query.abc ?? "",
      query.risk ?? "",
      query.type ?? "",
      query.view ?? "active",
      query.recordType ?? "",
      query.personType ?? "",
      query.businessModel ?? "",
      query.debtStatus ?? "",
      query.orderStatus ?? "",
      query.dataQuality ?? "",
      query.profileStatus ?? "",
      query.city ?? "",
      query.segment ?? "",
      query.archived ?? "exclude",
    ],
    queryFn: ({ signal }) => fetchClientList(query, signal),
    staleTime: ONE_MINUTE,
  });
}

/** A2 — Client360 compact envelope. */
export function useClient360(holdingId: string | undefined) {
  return useQuery({
    queryKey: ["client360", holdingId],
    queryFn: ({ signal }) => fetchClient360(holdingId as string, signal),
    enabled: !!holdingId,
    staleTime: ONE_MINUTE,
  });
}

/** Client360 Overview 2.0 (Этап 1) — dedicated lazy overview payload. */
export function useClient360Overview(holdingId: string | undefined) {
  return useQuery({
    queryKey: ["client360Overview", holdingId],
    queryFn: ({ signal }) => fetchClient360Overview(holdingId as string, signal),
    enabled: !!holdingId,
    staleTime: ONE_MINUTE,
  });
}

/** F-1 — legal-entity (Account) detail card. */
export function useLegalEntityDetail(accountId: string | undefined) {
  return useQuery({
    queryKey: ["legalEntityDetail", accountId],
    queryFn: ({ signal }) => fetchLegalEntityDetail(accountId as string, signal),
    enabled: !!accountId,
    staleTime: ONE_MINUTE,
  });
}

/** Этап 4 — «Структура» tree (Юрособи→Точки→Контакти). */
export function useStructure(holdingId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: ["structure", holdingId],
    queryFn: ({ signal }) => fetchStructure(holdingId as string, signal),
    enabled: !!holdingId && enabled,
    staleTime: ONE_MINUTE,
  });
}

/** A4 — manual client-type classification (F4B variant drawer). */
export function useClassifyClient(holdingId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: ClassifyClientPayload) =>
      classifyClient(holdingId as string, payload),
    onSuccess: () => {
      // Re-fetch the envelope + overview so the header pills, matrix resolver
      // (record_type / business_model drive missing/NBA) and tabs all re-render.
      qc.invalidateQueries({ queryKey: ["client360", holdingId] });
      qc.invalidateQueries({ queryKey: ["client360Overview", holdingId] });
      qc.invalidateQueries({ queryKey: ["clientList"] });
      // FIX C — класифікація драйвить ПОХІДНІ (3.0A-4, backend-owned): read-only
      // блок «Класифікація» + «Бізнес-профіль» (сценарій-гілка) = client360Profile;
      // %-complete + список «бракує» = businessProfile. Без цих двох картка
      // лишалась stale до reload (модель «—», % не оновлювались).
      qc.invalidateQueries({ queryKey: ["client360Profile", holdingId] });
      qc.invalidateQueries({ queryKey: ["businessProfile", holdingId] });
    },
  });
}

function invalidateOutlets(qc: ReturnType<typeof useQueryClient>, holdingId: string | undefined) {
  qc.invalidateQueries({ queryKey: ["outlets", holdingId] });
  qc.invalidateQueries({ queryKey: ["client360Overview", holdingId] });
  qc.invalidateQueries({ queryKey: ["client360", holdingId] });
}

/** FIX-1 — outlet quick-create (Огляд snapshot) + Этап 5 address-picker create. */
export function useCreateOutlet(holdingId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateOutletPayload) =>
      createOutlet(holdingId as string, payload),
    onSuccess: () => invalidateOutlets(qc, holdingId),
  });
}

/** Этап 5 — holding outlets list (Точки tab). */
export function useOutlets(holdingId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: ["outlets", holdingId],
    queryFn: ({ signal }) => fetchOutlets(holdingId as string, signal),
    enabled: !!holdingId && enabled,
    staleTime: ONE_MINUTE,
  });
}

/** Этап 5 — edit an outlet (CRM overlay; 1С official read-only enforced server-side). */
export function useUpdateOutlet(holdingId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ outletId, payload }: { outletId: string; payload: UpdateOutletPayload }) =>
      updateOutlet(outletId, payload),
    onSuccess: () => invalidateOutlets(qc, holdingId),
  });
}

/** Этап 5 — NP directory: areas (loaded once when the picker opens). */
export function useNpAreas(enabled: boolean) {
  return useQuery({
    queryKey: ["npAreas"],
    queryFn: ({ signal }) => fetchNpAreas(signal),
    enabled,
    staleTime: 60 * 60_000,
  });
}

/** NP settlements filtered by area (+ optional search). */
export function useNpSettlements(areaRef: string, q: string, enabled: boolean) {
  return useQuery({
    queryKey: ["npSettlements", areaRef, q],
    queryFn: ({ signal }) => fetchNpSettlements(areaRef, q, signal),
    enabled: enabled && (!!areaRef || q.length >= 2),
    staleTime: 10 * 60_000,
  });
}

/** NP streets — settlementRef REQUIRED (street_dependency_rule). */
export function useNpStreets(settlementRef: string, q: string, enabled: boolean) {
  return useQuery({
    queryKey: ["npStreets", settlementRef, q],
    queryFn: ({ signal }) => fetchNpStreets(settlementRef, q, signal),
    enabled: enabled && !!settlementRef,
    staleTime: 10 * 60_000,
  });
}

/** Этап 2 — Client360 Контакти aggregate (holding-master, ACL-safe). */
export function useClient360Contacts(
  holdingId: string | undefined,
  filters: { role?: string; incomplete?: boolean },
  enabled: boolean,
) {
  return useQuery({
    queryKey: ["client360Contacts", holdingId, filters.role ?? "", filters.incomplete ? "1" : ""],
    queryFn: ({ signal }) => fetchClient360Contacts(holdingId as string, filters, signal),
    enabled: !!holdingId && enabled,
    staleTime: ONE_MINUTE,
  });
}

/** Этап 2 — soft create contact (AC-2); re-fetches the aggregate + envelope counts. */
export function useCreateClient360Contact(holdingId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: Client360ContactCreatePayload) =>
      createClient360Contact(holdingId as string, payload),
    onSuccess: (res) => {
      if (res.status === "success") {
        qc.invalidateQueries({ queryKey: ["client360Contacts", holdingId] });
        qc.invalidateQueries({ queryKey: ["client360Overview", holdingId] });
        qc.invalidateQueries({ queryKey: ["client360", holdingId] });
      }
    },
  });
}

/** Этап 2 — edit contact (AC-5); re-fetches the aggregate. */
export function useUpdateClient360Contact(holdingId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { contactId: string; payload: Client360ContactUpdatePayload }) =>
      updateClient360Contact(v.contactId, v.payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["client360Contacts", holdingId] });
      qc.invalidateQueries({ queryKey: ["client360Overview", holdingId] });
    },
  });
}

/** Этап 2 — lifecycle status change (AC-6); re-fetches the aggregate. */
export function useChangeContactStatus(holdingId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { contactId: string; status: ContactStatus; note?: string }) =>
      changeClient360ContactStatus(v.contactId, v.status, v.note),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["client360Contacts", holdingId] });
      qc.invalidateQueries({ queryKey: ["client360Overview", holdingId] });
    },
  });
}

/** Этап 2 — manager request-review (AC-7); merge stays supervisor-only. */
export function useRequestContactReview(holdingId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { contactId: string; candidateId: string; reason?: string }) =>
      requestClient360ContactReview(v.contactId, v.candidateId, v.reason),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["client360Contacts", holdingId] });
    },
  });
}

/** A2 — lazy tab; only fetches when enabled (tab active). */
export function useClientTab<T = Record<string, unknown>>(
  holdingId: string | undefined,
  tab: ClientTabKey,
  enabled: boolean,
) {
  return useQuery({
    queryKey: ["clientTab", holdingId, tab],
    queryFn: ({ signal }) => fetchClientTab<T>(holdingId as string, tab, signal),
    enabled: !!holdingId && enabled,
    staleTime: ONE_MINUTE,
  });
}
